/**
 * Lightweight data-versions fetcher for CDN cache busting.
 *
 * Fetches /your_tour_api/data-versions/{urlSlug} (which bypasses CDN)
 * and returns version hashes that page components append as ?v= params
 * to their data URLs, ensuring CDN serves fresh data after content changes.
 *
 * The result is cached per urlSlug for the lifetime of the page — cleared
 * automatically on browser refresh.
 */

import { buildYourTourApiUrl, fetchYourTourApi } from './ApiHelpers';

// Module-level cache: { [cacheKey]: { promise, versions } }
// Cache keyed by urlSlug + mode so preview and normal sessions cache separately.
const cache = {};

const buildCacheKey = ( urlSlug, mode ) => ( mode ? `${urlSlug}::${mode}` : urlSlug );

/**
 * Fetch data versions for a community, returning the cached result if available.
 * Deduplicates concurrent requests for the same urlSlug.
 */
async function fetchVersions( urlSlug, platform, mode ) {
  const cacheKey = buildCacheKey( urlSlug, mode );

  if ( cache[cacheKey]?.versions ) {
    return cache[cacheKey].versions;
  }

  // Deduplicate: if a request is already in flight, wait for it.
  if ( cache[cacheKey]?.promise ) {
    return cache[cacheKey].promise;
  }

  const promise = ( async () => {
    try {
      const queryParams = mode ? { mode } : {};
      const url = buildYourTourApiUrl(
        `/your_tour_api/data-versions/${urlSlug}`, queryParams, platform
      );
      const data = await fetchYourTourApi( url, platform );
      const versions = data?.versions || {};
      cache[cacheKey] = { versions };
      return versions;
    } catch ( error ) {
      console.warn( '[dataVersions] Failed to fetch data versions:', error );
      // Cache empty object so we don't retry on every component mount.
      cache[cacheKey] = { versions: {} };
      return {};
    }
  } )();

  cache[cacheKey] = { promise };
  return promise;
}

/**
 * Get the version hash for a specific data type.
 *
 * @param {string} urlSlug - Community URL slug.
 * @param {string} versionKey - Key from the versions response
 *   ('amenities', 'floorPlans', 'media', 'map', etc.)
 * @param {string} [platform] - Optional platform override.
 * @param {string} [mode] - Optional mode (e.g., 'preview').
 * @returns {Promise<string|null>} The version hash, or null if unavailable.
 */
export async function getDataVersion( urlSlug, versionKey, platform, mode ) {
  if ( !urlSlug ) return null;

  const versions = await fetchVersions( urlSlug, platform, mode );
  return versions[versionKey] || null;
}

/**
 * Clear the cached versions (useful for testing or community switching).
 */
export function resetDataVersionsCache() {
  Object.keys( cache ).forEach( ( key ) => delete cache[key] );
}
